import React, { useState, useEffect } from 'react';
import { CheckCircle2, ArrowRight, ArrowLeft, User, Mail, Phone, MapPin, Clock, Calendar, Info, X, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import BookingSummary from './BookingSummary';

const InfoTooltip = ({ title, content }) => {
const [open, setOpen] = useState(false);
return (
<span className="relative inline-block ml-1 align-middle">
<button type="button" onClick={() => setOpen(!open)}
className="w-5 h-5 rounded-full inline-flex items-center justify-center"
style={{ backgroundColor: '#e8e4d9', color: '#888' }}>
<Info className="w-3 h-3" />
</button>
{open && (
<div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
<div className="w-80 p-5 rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}
style={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(201,168,76,0.3)' }}>
<div className="flex justify-between items-start mb-3">
<p className="font-bold" style={{ color: '#C9A84C' }}>{title}</p>
<button onClick={() => setOpen(false)}><X className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.5)' }} /></button>
</div>
<p className="text-sm" style={{ color: 'rgba(255,255,255,0.75)', lineHeight: '1.7' }}>{content}</p>
</div>
</div>
)}
</span>
);
};

const Counter = ({ value, onChange, min = 0, max = 20 }) => (
<div className="flex items-center gap-3">
<button type="button" onClick={() => onChange(Math.max(min, value - 1))}
className="w-9 h-9 rounded-full font-bold text-xl flex items-center justify-center transition-all"
style={{ backgroundColor: value > min ? '#1a1a2e' : '#e8e4d9', color: value > min ? '#C9A84C' : '#aaa' }}>−</button>
<span className="font-bold text-lg w-6 text-center" style={{ color: '#1a1a2e' }}>{value}</span>
<button type="button" onClick={() => onChange(Math.min(max, value + 1))}
className="w-9 h-9 rounded-full font-bold text-xl flex items-center justify-center"
style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>+</button>
</div>
);

const FAQ = ({ faqs, onNavigate }) => {
const [open, setOpen] = useState(null);
return (
<div className="space-y-3">
{faqs.map((faq, i) => (
<div key={i} className="rounded-xl overflow-hidden" style={{ border: '1px solid #e8e4d9' }}>
<button type="button" onClick={() => setOpen(open === i ? null : i)}
className="w-full p-4 text-left flex justify-between items-center"
style={{ backgroundColor: open === i ? '#1a1a2e' : '#fff' }}>
<span className="font-semibold text-sm" style={{ color: open === i ? '#fff' : '#1a1a2e' }}>{faq.q}</span>
{open === i ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: '#C9A84C' }} /> : <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: '#888' }} />}
</button>
{open === i && (
<div className="px-4 py-3" style={{ backgroundColor: '#f8f7f4' }}>
<p className="text-sm" style={{ color: '#555', lineHeight: '1.7' }}>{faq.a}</p>
{faq.link && onNavigate && (
<button onClick={() => onNavigate('momentlista', faq.link.key)}
className="mt-2 text-sm font-semibold underline"
style={{ color: '#C9A84C' }}>
→ {faq.link.label}
</button>
)}
</div>
)}
</div>
))}
</div>
);
};

const SERVICE_CONTENT = {
standard: {
title: 'Standardstädning',
included: [
{ room: 'Generellt i alla rum', items: ['Dammtorkar väggar och tar bort spindelväv', 'Dammsuger och våttorkar snickerier, element och fria ytor', 'Dammtorkar prydnadssaker, hyllor och tavlor', 'Dammtorkar eluttag och kontakter', 'Dammsuger stoppade möbler', 'Putsar speglar', 'Tömmer papperskorgar', 'Dammsuger och våttorkar golv'] },
{ room: 'Kök', items: ['Torkar av köksbänkar och luckor', 'Rengör diskho och blandare', 'Torkar av kakel och stänkskydd', 'Torkar av det som står framme'] },
{ room: 'Badrum', items: ['Rengör toalett, handfat och dusch/badkar', 'Putsar blandare och kranar', 'Torkar av alla ytor och speglar'] },
{ room: 'Hall', items: ['Dammsuger och torkar av skoställ', 'Torkar av hatthylla'] }
],
faqs: [
{ q: 'Hur ofta rekommenderar ni standardstädning?', a: 'Vi rekommenderar varannan vecka för bästa resultat. Med abonnemang sparar du upp till 15% per städning.' },
{ q: 'Tar ni med städmedel?', a: 'Ja! Vi tar med alla städmedel. Du behöver ha dammsugare, mopp och hink.' },
{ q: 'Vad händer om jag inte är nöjd?', a: 'Vi erbjuder 100% nöjdhetsgaranti — städar om utan kostnad.' },
{ q: 'Vad ingår exakt i standardstädningen?', a: 'Se vår kompletta momentlista för exakt vad som ingår i varje rum.', link: { label: 'Standardstädning — Momentlista', key: 'standardstadning' } }
],
tips: ['Ha det undanplockat när vi kommer', 'Informera oss om känsliga material som marmor', 'Vi lyfter inte tunga möbler']
},
deep: {
title: 'Djupstädning',
included: [
{ room: 'Generellt i alla rum', items: ['Dammtorkar väggar och tar bort spindelväv', 'Dammsuger och våttorkar snickerier, element, fria ytor samt ovanpå garderober', 'Dammtorkar prydnadssaker, hyllor och tavlor', 'Dammtorkar eluttag och kontakter', 'Dammsuger stoppade möbler', 'Dammsuger på och under mattor (om möjligt)', 'Dammtorkar lampor', 'Torrdammar elektronik (ej på bildskärm)', 'Putsar speglar', 'Tömmer papperskorgar', 'Dammsuger och våttorkar golv'] },
{ room: 'Kök', items: ['Rengör fläkt och fläktfilter (ej kolfilter)', 'Rengör ovanpå skåp och hyllor', 'Ut- och invändig avtorkning av köksluckor', 'Rengör kyl och frys utvändigt', 'Rengör kakel och stänkskydd', 'Rengör under skåp och torkar av socklar', 'Ut- och invändig rengöring av mikrovågsugn', 'Ut- och invändig avtorkning av diskmaskin', 'Torkar av det som står framme', 'Torkar ur besticklådor', 'Tömmer sopor och torkar sopkorg', 'Putsar diskho, blandare och sil'] },
{ room: 'Sovrum', items: ['Bäddar sängar', 'Bäddar rent om nya lakan ligger framme', 'Dammtorkar sänggavel och -karm'] },
{ room: 'Badrum', items: ['Torkar av synliga rör', 'Putsar blandare, kran och dusch', 'Avfettar och avkalkar fogar i duschutrymme', 'Rengör badkar och tar bort front', 'Rengör golvbrunn', 'Rengör kran och handfat', 'Rengör hela toalettstolen', 'Rengör utsidan av vitvaror', 'Torkar av flaskor som står framme'] },
{ room: 'Hall', items: ['Dammtorkar ledstång, räcke och spjälor i trappa', 'Dammsuger och torkar av skoställ och hatthylla', 'Torkar av proppskåp'] }
],
faqs: [
{ q: 'Behöver jag förbereda något?', a: 'Ha det undanplockat när vi kommer för bästa resultat.' },
{ q: 'Tar ni med städmedel?', a: 'Ja! Du behöver ha dammsugare, mopp och hink. Saknar du det tar vi med mot tilläggskostnad.' },
{ q: 'Kan ni ta bort fläckar på väggarna?', a: 'Vi dammtorkar väggar men kan inte ta bort fläckar som inte går bort med en dammtrasa.' },
{ q: 'Städar ni byggdamm?', a: 'Nej. Grovstädning behöver göras först.' },
{ q: 'Vad ingår exakt i djupstädningen?', a: 'Se vår kompletta momentlista för exakt vad som ingår i varje rum, vilka tilläggstjänster som finns och vad du bör tänka på.', link: { label: 'Djupstädning — Momentlista', key: 'djupstadning' } }

],
tips: ['Ha det undanplockat', 'Informera om känsliga material', 'Dra ut vitvaror om du vill att vi rengör bakom', 'Vi lyfter inte tunga möbler', '2 arbetsdagars garanti']
},
moving: {
title: 'Flyttstädning',
included: [
{ room: 'Hela bostaden', items: ['Dammtorkar väggar, tak och tar bort spindelväv i alla rum', 'Rengör alla dörrar, dörrkarmar och handtag', 'Putsar alla speglar och glaspartier', 'Dammsuger och våttorkar alla golv noggrant'] },
{ room: 'Kök — grundlig genomgång', items: ['Rengör fläkt och fläktfilter fullständigt', 'Torkar ut- och invändigt alla köksluckor och lådor', 'Rengör kyl och frys ut- och invändigt', 'Rengör ugn och spis ut- och invändigt', 'Rengör mikrovågsugn och diskmaskin ut- och invändigt', 'Putsar diskho, blandare och sil grundligt'] },
{ room: 'Badrum — detaljerad rengöring', items: ['Avfettar och avkalkar hela duschutrymmet', 'Rengör och avkalkar toalettstolen fullständigt', 'Rengör badkar och under front', 'Rengör golvbrunn och avlopp', 'Putsar alla kranar och blandare', 'Rengör tvättmaskin ut- och invändigt', 'Skurar golv och väggar noggrant'] },
{ room: 'Sovrum', items: ['Rengör inuti alla garderober och skåp', 'Dammtorkar sänggavel', 'Rengör fönsterbrädor och karmar'] },
{ room: 'Hall & övriga', items: ['Dammtorkar ledstång och räcke', 'Rengör skoställ och hatthylla', 'Torkar av entrédörr in- och utsida', 'Balkongen sopas och torkas (om möjligt)'] }
],
faqs: [
{ q: 'Garanteras att besiktningen godkänns?', a: '100% nöjdhetsgaranti — städar om kostnadsfritt inom 2 arbetsdagar.' },
{ q: 'Hur lång tid tar det?', a: '6-10 timmar beroende på storlek och skick.' },
{ q: 'Behöver jag vara hemma?', a: 'Nej. Vi dokumenterar med foton och lämnar tillbaka nyckeln säkert.' },
{ q: 'Tar ni med städmedel?', a: 'Ja, allt ingår. Saknar du dammsugare, mopp eller hink tar vi med mot tilläggskostnad.' },
{ q: 'Vad ingår exakt i flyttstädningen?', a: 'Se vår kompletta momentlista för exakt vad som ingår — vi har en av marknadens mest detaljerade momentlistor.', link: { label: 'Flyttstädning — Momentlista', key: 'flyttstadning' } }
],
tips: ['Bostaden ska vara tom på möbler', 'Vi fotograferar före och efter', 'Boka minst 3 dagar i förväg']
},
office: {
title: 'Kontorsstädning',
included: [
{ room: 'Generellt', items: ['Dammtorkar alla ytor, hyllor och möbler', 'Torrdammar elektronik (ej skärmar)', 'Dammsuger och våttorkar golv', 'Tömmer papperskorgar', 'Putsar speglar och glaspartier', 'Torkar av dörrhandtag'] },
{ room: 'Kök och personalrum', items: ['Torkar av köksbänkar och luckor', 'Rengör diskho och blandare', 'Torkar ur mikrovågsugn', 'Torkar av kakel'] },
{ room: 'Toaletter', items: ['Rengör toalettstolar fullständigt', 'Rengör handfat och kranar', 'Torkar alla ytor', 'Rengör golv noggrant'] }
],
faqs: [
{ q: 'Kan ni städa utanför kontorstid?', a: 'Ja! Vi erbjuder flexibla tider — tidigt på morgonen, sent på kvällen eller helger.' },
{ q: 'Gäller RUT-avdrag?', a: 'Nej, men kontorsstädning är avdragsgill som företagskostnad.' },
{ q: 'Kan vi ha ett löpande avtal?', a: 'Absolut! Vi erbjuder fasta abonnemang anpassade efter era behov.' },
{ q: 'Vad ingår exakt i kontorsstädningen?', a: 'Se vår kompletta momentlista för kontorsstädning.', link: { label: 'Kontorsstädning — Momentlista', key: 'kontorsstadning' } }
],
tips: ['Vi anpassar städning efter era behov', 'Avdragsgill som företagskostnad', 'Diskreta och professionella']
},
help: {
title: 'Flytthjälp',
included: [
{ room: 'Vad ingår', items: ['Packning och nedpackning av bohag', 'Bärhjälp — vi lastar och lyfter', 'Transport av möbler och lösöre', 'Uppackning i nytt hem om önskas', 'Demontering och montering av enklare möbler', 'Skyddstäckning av känsliga föremål'] }
],
faqs: [
{ q: 'Hur beräknas priset?', a: 'Per person och timme. Inkluderar arbetstid men inte materialkostnader.' },
{ q: 'Ingår transport?', a: 'Nej, fordonshyra och drivmedel tillkommer enligt offert.' },
{ q: 'Kan ni hantera tunga möbler?', a: 'Ja, med rätt utrustning.' },
{ q: 'Vad ingår exakt i flytthjälpen?', a: 'Se vår kompletta momentlista för exakt vad som ingår och inte ingår.', link: { label: 'Flytthjälp — Momentlista', key: 'flytthjalp' } }
],
tips: ['Boka i god tid', 'Packa lådor i förväg', 'Informera om trappor och parkering']
},
window: {
title: 'Fönsterputs',
included: [
{ room: 'Vad ingår', items: ['Professionell putsning av alla angivna fönster', 'Vi tar med all utrustning', 'Kan utföras även vid regn eller snö', 'Priset baseras på antal och typ'] }
],
faqs: [
{ q: 'Hur räknas fönstren?', a: 'Varje fönsterbåge räknas som ett fönster.' },
{ q: 'Ingår karmtvätt?', a: 'Karmtvätt är ett tillägg för 95 kr per fönster. Rekommenderas.' },
{ q: 'Dubbelsidigt vs enkelsidigt?', a: 'Dubbelsidigt = båda sidor. Enkelsidigt = en sida.' },
{ q: 'Behövs stege?', a: 'Vi tar med stege mot 295 kr tillägg.' },
{ q: 'Vad ingår exakt i fönsterputs?', a: 'Se vår kompletta momentlista med alla fönstertyper och priser.', link: { label: 'Fönsterputs — Momentlista', key: 'fonsterputs' } }
],
tips: ['Ange rätt antal och typ', 'Karmtvätt rekommenderas', 'Vi putsar även vid dåligt väder']
},
package: {
title: 'Månadspaket',
included: [
{ room: 'Vecka 1 — Djupstädning', items: ['Komplett djupstädning av hela hemmet', 'Alla rum genomgås noggrant', 'Kök och badrum extra grundligt'] },
{ room: 'Vecka 2, 3 & 4 — Standardstädning', items: ['Regelbunden hemstädning varje vecka', 'Alla rum dammsugs och torkas', 'Kök och badrum rengörs', 'Golv dammsugs och våttorkas'] }
],
faqs: [
{ q: 'Vad ingår?', a: 'En djupstädning vecka 1 + tre standardstädningar veckorna 2, 3 och 4.' },
{ q: 'Är det bindningstid?', a: 'Nej, löpande avtal. Avsluta med en månads varsel.' },
{ q: 'Samma städare varje gång?', a: 'Vi strävar alltid efter samma team.' },
{ q: 'Hur mycket sparar jag?', a: 'Ca 2 000 kr/mån jämfört med att boka varje städning separat.' },
{ q: 'Vad ingår exakt i månadspaketet?', a: 'Se vår kompletta momentlista för månadspaketet.', link: { label: 'Månadspaket — Momentlista', key: 'manadspaket' } }
],
tips: ['Perfekt för ett alltid rent hem', 'Spara ~2 000 kr/mån', 'Samma team varje vecka']
},
minipaket: {
title: 'Minipaket',
included: [
{ room: 'Varannan vecka — Djupstädning', items: ['Dammtorkning av väggar och borttagning av spindelväv', 'Dammsugning och avtorkning av snickerier, element och fria ytor', 'Rengöring av fläkt och fläktfilter i kök', 'Ut- och invändig avtorkning av köksluckor', 'Avfettning och avkalkning av fogar i badrum', 'Rengöring av golvbrunn och toalettstol', 'Bäddning av sängar', 'Dammsugning och våttorkning av alla golv'] },
{ room: 'Varannan vecka — Standardstädning', items: ['Dammtorkning av alla ytor och hyllor', 'Avtorkning av köksbänkar och luckor', 'Rengöring av diskho och blandare', 'Rengöring av toalett, handfat och dusch', 'Tömning av sopor', 'Bäddning av sängar', 'Dammsugning och våttorkning av alla golv'] }
],
faqs: [
{ q: 'Hur fungerar minipaketet?', a: 'En djupstädning varannan vecka och en standardstädning varannan vecka — totalt 2 städningar per månad.' },
{ q: 'Är det bindningstid?', a: 'Nej, löpande avtal. Avsluta med en månads varsel.' },
{ q: 'Vad ingår exakt?', a: 'Se vår kompletta momentlista för minipaketet.', link: { label: 'Minipaket — Momentlista', key: 'minipaket' } }
],
tips: ['Ha det undanplockat varje städdag', 'Samma team varannan vecka', 'Faktureras månadsvis i förväg']
}
};


const BookingForm = ({ onBookingComplete, preselectService, onClearPreselect, currentUser, onNavigate }) => {
const [step, setStep] = useState(1);
const [availableTimes, setAvailableTimes] = useState([]);
const [blockedDates, setBlockedDates] = useState([]);
const [bookedSlots, setBookedSlots] = useState([]);
const [dateWarning, setDateWarning] = useState('');
const [pnrError, setPnrError] = useState('');
const [discountPercent, setDiscountPercent] = useState(0);
const [showIncluded, setShowIncluded] = useState(false);
const [discountCode, setDiscountCode] = useState('');
const [discountError, setDiscountError] = useState('');
const [appliedDiscount, setAppliedDiscount] = useState(null);
// package flow: 'deep' | 'deepWindow' | 'standard' | 'standardWindow'
const [packagePhase, setPackagePhase] = useState('deep');

const stepRef = React.useRef(null);

const initWindows = () => ({
utan: { count: 0, priceDouble: 195, priceSingle: 120, label: 'Utan spröjs' },
med: { count: 0, priceDouble: 245, priceSingle: 155, label: 'Med spröjs' },
overhanging: { count: 0, priceDouble: 275, priceSingle: 175, label: 'Överhängande fönster' },
balkong: { count: 0, priceDouble: 895, priceSingle: 495, label: 'Inglasad balkong' }
});

const [bookingData, setBookingData] = useState({
service: '', subscription: 'once', sqm: '', hours: '',
name: '', email: '', phone: '', personnummer: '',
address: '', city: 'Motala', postalCode: '',
date: '', time: '', notes: '', extraNotes: '',
price: 0, baseServicePrice: 0, rutDiscount: 0, finalPrice: 0,
needsEquipment: false,
windowCleaning: false,
windows: initWindows(),
windowSides: {},
balkongType: {},
karmtvätt: false, stege: false,
ovenCount: 0, fridgeCount: 0,
hasPets: null, petTypes: [],
keyHandling: '', currentWeek: 0,
packageDeepAddons: {
ovenCount: 0, fridgeCount: 0, windowCleaning: false,
windows: initWindows(), karmtvätt: false, stege: false
},
packageStandardAddons: {
ovenCount: 0, fridgeCount: 0, windowCleaning: false,
windows: initWindows(), karmtvätt: false, stege: false
}
});

const rates = { standard: 65, deep: 80, moving: 95, office: 75 };
const minPrices = { standard: 3490, deep: 5490, moving: 7490, office: 4490, help: 2250 };
const PACKAGE_RATE = 250;
const PACKAGE_MIN = 12500;
const MINI_RATE = 135;
const MINI_MIN = 6750;
const noRutServices = ['office'];
const noSubscriptionServices = ['moving', 'help', 'minipaket'];

const serviceNames = {
standard: 'Standardstädning', deep: 'Djupstädning',
moving: 'Flyttstädning', office: 'Kontorsstädning',
help: 'Flytthjälp', window: 'Fönsterputs', package: 'Månadspaket',
minipaket: 'Minipaket'
};

const subscriptionDiscount = { once: 0, weekly: 0.15, biweekly: 0.10, triweekly: 0.05 };

useEffect(() => {
  setAvailableTimes(['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00']);
  supabase.from('settings').select('key, value').then(({ data }) => {
    (data || []).forEach(r => {
      if (r.key === 'available-times' && Array.isArray(r.value) && r.value.length) setAvailableTimes(r.value);
      if (r.key === 'blocked-dates' && Array.isArray(r.value)) setBlockedDates(r.value);
    });
  });
  supabase.from('bookings').select('date, time, status').neq('status', 'cancelled').then(({ data }) => {
    setBookedSlots((data || []).filter(b => b.date && b.time).map(b => `${b.date}_${b.time}`));
  });
}, []);

useEffect(() => {
if (currentUser?.role === 'customer') {
setBookingData(prev => ({
...prev,
name: currentUser.name || prev.name,
email: currentUser.email || prev.email,
phone: currentUser.phone || prev.phone,
address: currentUser.address || prev.address,
city: currentUser.city || prev.city,
postalCode: currentUser.postal_code || prev.postalCode
}));
}
}, [currentUser]);

useEffect(() => {
if (preselectService) {
setBookingData(prev => ({ ...prev, service: preselectService }));
if (onClearPreselect) onClearPreselect();
}
}, [preselectService]); // eslint-disable-line react-hooks/exhaustive-deps

const hasRut = (s) => !noRutServices.includes(s);

const calcWindowAddonTotal = (windows, karmtvätt, stege, windowSides, prefix = '') => {
let total = 0;
let totalCount = 0;
if (windows) {
Object.entries(windows).forEach(([type, w]) => {
if (w.count > 0) {
const sideKey = prefix ? `${prefix}_${type}` : type;
const isDouble = (windowSides?.[sideKey] || 'double') === 'double';
total += w.count * (isDouble ? (w.priceDouble || 195) : (w.priceSingle || 120));
totalCount += w.count;
}
});
}
if (karmtvätt) total += totalCount * 95;
if (stege) total += 295;
return total;
};

const calcPrice = (data) => {
if (data.service === 'minipaket') {
const sqm = parseInt(data.sqm) || 0;
const base = Math.max(sqm * MINI_RATE, MINI_MIN);
const subDisc = subscriptionDiscount[data.subscription] || 0;
const afterSub = base * (1 - subDisc);
const addonTotal =
(data.ovenCount || 0) * 490 * 2 +
(data.fridgeCount || 0) * 365 * 2 +
(data.needsEquipment ? 400 * 2 : 0) +
calcWindowAddonTotal(data.windows, data.karmtvätt, data.stege, data.windowSides);
const total = afterSub + addonTotal;
const rutDiscount = total * 0.50;
return { basePrice: total, rutDiscount, finalPrice: total - rutDiscount };
}
if (data.service === 'package') {
const deepAddonTotal =
calcWindowAddonTotal(data.packageDeepAddons?.windows, data.packageDeepAddons?.karmtvätt, data.packageDeepAddons?.stege, data.windowSides, 'deep') +
(data.packageDeepAddons?.ovenCount || 0) * 490 +
(data.packageDeepAddons?.fridgeCount || 0) * 365;
const standardAddonTotal =
calcWindowAddonTotal(data.packageStandardAddons?.windows, data.packageStandardAddons?.karmtvätt, data.packageStandardAddons?.stege, data.windowSides, 'std') * 3 +
(data.packageStandardAddons?.ovenCount || 0) * 490 * 3 +
(data.packageStandardAddons?.fridgeCount || 0) * 365 * 3;
const equipmentCost = data.needsEquipment ? 400 * 4 : 0;
const windowTotal = calcWindowAddonTotal(data.windows, data.karmtvätt, data.stege, data.windowSides);
      const packageBase = Math.max((parseInt(data.sqm) || 0) * PACKAGE_RATE, PACKAGE_MIN);
      const total = packageBase + deepAddonTotal + standardAddonTotal + equipmentCost + windowTotal;
const rutDiscount = total * 0.50;
return { basePrice: total, rutDiscount, finalPrice: total - rutDiscount };
}
if (data.service === 'help') {
const basePrice = Math.max((parseInt(data.hours) || 0) * 450, minPrices.help);
const rutDiscount = basePrice * 0.50;
return { basePrice, rutDiscount, finalPrice: basePrice - rutDiscount };
}
if (data.service === 'window') {
const windowTotal = calcWindowAddonTotal(data.windows, data.karmtvätt, data.stege, data.windowSides);
const basePrice = Math.max(windowTotal, 1599);
const subDisc = subscriptionDiscount[data.subscription] || 0;
const afterSub = basePrice * (1 - subDisc);
const rutDiscount = afterSub * 0.50;
return { basePrice: afterSub, rutDiscount, finalPrice: afterSub - rutDiscount };
}
if (!data.service) return { basePrice: 0, rutDiscount: 0, finalPrice: 0 };
const sqm = parseInt(data.sqm) || 0;
let base = Math.max(sqm * (rates[data.service] || 80), minPrices[data.service] || 5490);
const subDisc = subscriptionDiscount[data.subscription] || 0;
base = base * (1 - subDisc);
const addonTotal =
calcWindowAddonTotal(data.windows, data.karmtvätt, data.stege, data.windowSides) +
(data.ovenCount || 0) * 490 +
(data.fridgeCount || 0) * 365 +
(data.needsEquipment ? 400 : 0);
const total = base + addonTotal;
const rutDiscount = hasRut(data.service) ? total * 0.50 : 0;
return { basePrice: total, rutDiscount, finalPrice: total - rutDiscount };
};

const recalc = (data) => {
const { basePrice, rutDiscount, finalPrice } = calcPrice(data);
return { ...data, price: basePrice, baseServicePrice: basePrice, rutDiscount, finalPrice };
};

const handleChange = (field, value) => {
let newData = { ...bookingData, [field]: value };
if (field === 'service') {
  newData.subscription = 'once'; newData.sqm = ''; newData.hours = '';
  newData.windowCleaning = false; newData.windows = initWindows(); newData.windowSides = {};
  newData.karmtvätt = false; newData.stege = false;
  newData.needsEquipment = false; newData.ovenCount = 0; newData.fridgeCount = 0;
}
if (['sqm','hours','service','subscription','ovenCount','fridgeCount','windowCleaning','karmtvätt','stege','needsEquipment'].includes(field)) {
newData = recalc(newData);
}
if (field === 'personnummer') { newData.personnummer = formatPnr(value); setPnrError(''); }
if (field === 'date') {
newData.time = '';
setDateWarning(value && isTodayOrPast(value) ? '🚫 Vi kan tyvärr inte boka samma dag. Välj tidigast imorgon.' : value && isWithin48h(value) ? '⏰ Välj helst minst 48h fram för bäst kvalitet!' : '');
}
setBookingData(newData);
};

const updateWindow = (type, field, value) => {
const newWindows = { ...bookingData.windows, [type]: { ...bookingData.windows[type], [field]: value } };
setBookingData(recalc({ ...bookingData, windows: newWindows }));
};

const updateWindowSide = (type, side) => {
const newSides = { ...bookingData.windowSides, [type]: side };
setBookingData(recalc({ ...bookingData, windowSides: newSides }));
};

const updatePackageDeep = (field, value) => {
const newAddons = { ...bookingData.packageDeepAddons, [field]: value };
setBookingData(recalc({ ...bookingData, packageDeepAddons: newAddons }));
};

const updatePackageStandard = (field, value) => {
const newAddons = { ...bookingData.packageStandardAddons, [field]: value };
setBookingData(recalc({ ...bookingData, packageStandardAddons: newAddons }));
};

const scrollToStep = () => {
setTimeout(() => {
if (stepRef.current) {
stepRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
}, 50);
};

const goToStep = (s) => {
setStep(s);
setShowIncluded(false);
scrollToStep();
};

const changePackagePhase = (phase) => {
setPackagePhase(phase);
setShowIncluded(false);
scrollToStep();
};

const formatPnr = (v) => { const c = v.replace(/\D/g, ''); return c.length <= 8 ? c : c.slice(0,8) + '-' + c.slice(8,12); };

const validatePnr = (p) => {
const c = p.replace(/[-\s]/g, '');
if (!/^\d{10,12}$/.test(c)) return false;
const d = c.length === 12 ? c.slice(2) : c;
if (d.length !== 10) return false;
const nums = d.split('').map(Number);
let sum = 0;
for (let i = 0; i < 9; i++) { let n = nums[i] * (i % 2 === 0 ? 2 : 1); if (n > 9) n -= 9; sum += n; }
return (10 - (sum % 10)) % 10 === nums[9];
};

const isInMotala = (postal) => {
  const p = (postal || '').replace(/\s/g, '');
  const OSTERGOTLAND = ['570','573','580','581','582','583','584','585','586','587','588','589','590','591','592','595','596','597','599','600','601','602','603','604','605','606','607','608','609','610','612','614','615','616','618'];
  return p.length === 5 && OSTERGOTLAND.includes(p.slice(0, 3));
};

const localDateStr = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
const isTodayOrPast = (d) => d <= localDateStr(new Date());
const isWithin48h = (d) => (new Date(d) - new Date()) / 3600000 < 48;
const isTimeBooked = (d, t) => bookedSlots.includes(`${d}_${t}`);
const isDateBlocked = (d) => blockedDates.includes(d);

const hasValidInput = () => {
if (bookingData.service === 'help') return bookingData.hours && parseInt(bookingData.hours) > 0;
if (bookingData.service === 'window') return true;
if (bookingData.service === 'package') return true;
if (bookingData.service === 'minipaket') return bookingData.sqm && parseInt(bookingData.sqm) > 0;
return bookingData.sqm && parseInt(bookingData.sqm) > 0;
};

const validateInfo = () => {
if (!bookingData.name || !bookingData.email || !bookingData.phone || !bookingData.address) return false;
if (isTodayOrPast(bookingData.date)) { alert('Vi kan tyvärr inte boka samma dag. Gå tillbaka och välj ett senare datum.'); return false; }
if (!bookingData.personnummer) { setPnrError('Personnummer krävs'); return false; }
if (!validatePnr(bookingData.personnummer)) { setPnrError('Ogiltigt personnummer'); return false; }
if (!isInMotala(bookingData.postalCode)) { alert('Tyvärr städar vi bara inom Östergötland. Kontrollera ditt postnummer.'); return false; }
setPnrError('');
return true;
};

const handleSubmit = async () => {
try {
const k = 1 - discountPercent / 100;
const booking = {
  ...bookingData,
  price: bookingData.price * k, rutDiscount: bookingData.rutDiscount * k, finalPrice: bookingData.finalPrice * k,
  id: `booking_${Date.now()}_${crypto.randomUUID().replace(/-/g, '')}`, createdAt: new Date().toISOString(), status: 'upcoming',
  discountPercent, discountCode: appliedDiscount ? appliedDiscount.code : ''
};

const { error: dbError } = await supabase.from('bookings').insert([{
id: booking.id, name: booking.name, email: booking.email, phone: booking.phone,
address: booking.address, city: booking.city, postal_code: booking.postalCode,
service: booking.service, sqm: parseInt(booking.sqm) || null,
date: booking.date, time: booking.time, notes: booking.notes,
price: booking.price, rut_discount: booking.rutDiscount, final_price: booking.finalPrice,
personnummer: booking.personnummer, status: 'upcoming', created_at: booking.createdAt,
details: (({ personnummer, ...rest }) => rest)(booking)
}]);
if (dbError) throw dbError;
await fetch('/.netlify/functions/send-email', {
method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ bookingId: booking.id })
});
goToStep(99);
if (onBookingComplete) onBookingComplete(booking);
} catch (error) { console.error(error); alert(error && error.message ? error.message : 'Något gick fel. Försök igen.'); }
};

const resetBooking = () => {
setBookingData({
service: '', subscription: 'once', sqm: '', hours: '',
name: currentUser?.name || '', email: currentUser?.email || '', phone: currentUser?.phone || '',
personnummer: '', address: '', city: 'Motala', postalCode: '', date: '', time: '',
notes: '', extraNotes: '', price: 0, baseServicePrice: 0, rutDiscount: 0, finalPrice: 0,
needsEquipment: false, windowCleaning: false, windows: initWindows(),
windowSides: {}, balkongType: {}, karmtvätt: false, stege: false,
ovenCount: 0, fridgeCount: 0, hasPets: null, petTypes: [], keyHandling: '', currentWeek: 0,
packageDeepAddons: { ovenCount: 0, fridgeCount: 0, windowCleaning: false, windows: initWindows(), karmtvätt: false, stege: false },
packageStandardAddons: { ovenCount: 0, fridgeCount: 0, windowCleaning: false, windows: initWindows(), karmtvätt: false, stege: false }
});
setStep(1); setPnrError(''); setDateWarning(''); setDiscountPercent(0);
setPackagePhase('deep'); setDiscountCode(''); setAppliedDiscount(null); setDiscountError('');
scrollToStep();
};

const inputStyle = { width: '100%', padding: '12px 16px', borderRadius: '12px', border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4', color: '#1a1a2e', outline: 'none', fontSize: '16px' };
const cardStyle = { backgroundColor: '#fff', border: '1px solid #e8e4d9', borderRadius: '20px', padding: '28px' };
const discountAmount = appliedDiscount ? Math.round(bookingData.price * appliedDiscount.discount_percent / 100 * (hasRut(bookingData.service) ? 0.5 : 1)) : 0;
const finalAfterCode = bookingData.finalPrice - discountAmount;

const handleApplyDiscount = async () => {
if (!discountCode.trim()) return;
try {
const { data } = await supabase
.from('discount_codes')
.select('*')
.eq('code', discountCode.toUpperCase().trim())
.eq('active', true)
.single();
if (!data) { setDiscountError('Ogiltig rabattkod'); setAppliedDiscount(null); setDiscountPercent(0); return; }
if (data.expires_at && new Date(data.expires_at) < new Date()) { setDiscountError('Rabattkoden har gått ut'); setAppliedDiscount(null); setDiscountPercent(0); return; }
setAppliedDiscount(data);
setDiscountError('');
setDiscountPercent(data.discount_percent);
} catch { setDiscountError('Kunde inte validera koden'); }
};

const sectionDivider = { borderTop: '2px solid #e8e4d9', marginTop: '32px', paddingTop: '32px' };
const currentContent = SERVICE_CONTENT[bookingData.service] || SERVICE_CONTENT.deep;

const WhatIsIncluded = ({ serviceKey }) => {
const content = SERVICE_CONTENT[serviceKey] || currentContent;
return (
<div style={sectionDivider}>
<button onClick={() => setShowIncluded(!showIncluded)} className="w-full flex justify-between items-center mb-4">
<h3 className="text-lg font-bold text-left">{content.title} — Vad ingår?</h3>
{showIncluded ? <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: '#C9A84C' }} /> : <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: '#C9A84C' }} />}
</button>
{showIncluded && (
<div>
{content.included.map((section, i) => (
<div key={i} className="mb-5">
<p className="font-bold mb-2" style={{ color: '#1a1a2e' }}>{section.room}</p>
<ul className="space-y-1.5">
{section.items.map((item, j) => (
<li key={j} className="flex items-start gap-2 text-sm" style={{ color: '#555' }}>
<span style={{ color: '#C9A84C', marginTop: '2px', flexShrink: 0 }}>✓</span>{item}
</li>
))}
</ul>
</div>
))}
{content.tips && (
<div className="p-4 rounded-xl mb-4" style={{ backgroundColor: '#fef9ec', border: '1px solid #fde68a' }}>
<p className="font-bold text-sm mb-2" style={{ color: '#92400e' }}>💡 Att tänka på</p>
<ul className="space-y-1">
{content.tips.map((tip, i) => (<li key={i} className="text-xs" style={{ color: '#92400e' }}>• {tip}</li>))}
</ul>
</div>
)}
<FAQ faqs={content.faqs} onNavigate={onNavigate} />
</div>
)}
</div>
);
};

return (
<div className="max-w-6xl mx-auto px-4 pt-8 pb-36 lg:pb-8" style={{ color: '#1a1a2e' }}>
<div className="lg:flex lg:gap-8 lg:items-start">
<div className="flex-1 min-w-0">
<div ref={stepRef} style={{ scrollMarginTop: '80px' }} />

{/* Progress bar */}
{step !== 99 && (
<div className="mb-8">
<div className="flex justify-between text-xs mb-2">
<span className="font-semibold" style={{ color: '#C9A84C' }}>
{step === 1 ? 'Välj Tjänst' : step === 2 ? (bookingData.service === 'package' ? 'Anpassa Paket' : 'Välj Abonnemang') : step === 3 ? 'Tillägg' : step === 'W' ? 'Fönsterputs' : step === 4 ? 'Datum & Tid' : 'Dina Uppgifter'}
</span>
</div>
<div className="h-2 rounded-full" style={{ backgroundColor: '#e8e4d9' }}>
<div className="h-2 rounded-full transition-all duration-300"
style={{
width: step === 1 ? '16%' : step === 2 ? '32%' : step === 3 ? '48%' : step === 'W' ? '60%' : step === 4 ? '72%' : step === 5 ? '88%' : '100%',
backgroundColor: '#C9A84C'
}} />
</div>
</div>
)}

{/* ── STEP 1: Choose service ─────────────────────────────────── */}
{step === 1 && (
<div style={cardStyle}>
<h2 className="text-2xl font-bold mb-2">Välj Tjänst</h2>
<p className="text-sm mb-6" style={{ color: '#888' }}>Välj den tjänst som passar dig bäst</p>
<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
{[
{ id: 'package', name: '⭐ Månadspaket', rate: 'från 250 kr/m²', desc: 'Djupstäd + 3× Standard — allt ingår!', rut: true, highlight: true },
{ id: 'deep', name: 'Djupstädning', rate: 'från 80 kr/m²', desc: 'Grundlig genomgång av hela hemmet', popular: true, rut: true },
{ id: 'standard', name: 'Standardstädning', rate: 'från 65 kr/m²', desc: 'Regelbunden hemstädning', rut: true },
{ id: 'moving', name: 'Flyttstädning', rate: 'från 95 kr/m²', desc: 'Garanterat godkänd vid besiktning', rut: true },
{ id: 'window', name: 'Fönsterputs', rate: 'från 120 kr/fönster', desc: 'Professionell fönsterputsning', rut: true },
{ id: 'office', name: 'Kontorsstädning', rate: 'från 75 kr/m²', desc: 'För kontor och lokaler', rut: false },
{ id: 'help', name: 'Flytthjälp', rate: '450 kr/h/person', desc: 'Packning, bärhjälp & transport', rut: true }
,{ id: 'minipaket', name: '🌟 Minipaket', rate: 'från 135 kr/m²', desc: 'Djupstäd + Standard varannan vecka', rut: true }
].map(s => (
<button key={s.id} onClick={() => handleChange('service', s.id)}
className={`p-4 rounded-xl text-left transition-all ${s.id === 'package' ? 'sm:col-span-2' : ''}`}
style={{
border: `2px solid ${bookingData.service === s.id ? '#C9A84C' : s.highlight ? '#C9A84C' : '#e8e4d9'}`,
backgroundColor: bookingData.service === s.id ? 'rgba(201,168,76,0.12)' : s.highlight ? '#fffbeb' : '#f8f7f4'
}}>
<div className="flex gap-2 mb-2 flex-wrap">
{s.popular && <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>POPULÄR</span>}
{s.highlight && <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>MEST POPULÄRT</span>}
{s.rut ? <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#dcfce7', color: '#16a34a' }}>50% RUT</span>
: <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}>Ingen RUT</span>}
</div>
<h3 className="font-bold mb-1">{s.name}</h3>
<p className="text-sm font-semibold" style={{ color: '#C9A84C' }}>{s.rate}</p>
<p className="text-xs" style={{ color: '#888' }}>{s.desc}</p>
{s.id === 'package' && (
<div className="flex gap-2 mt-2 flex-wrap">
{['Djupstäd vecka 1','Standard vecka 2','Standard vecka 3','Standard vecka 4'].map((w,i) => (
<span key={i} className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>{w}</span>
))}
</div>
)}
</button>
))}
</div>

{bookingData.service && bookingData.service !== 'window' && bookingData.service !== 'package' && bookingData.service !== 'minipaket' && (
<div className="mb-6">
<label className="block text-sm font-bold mb-2">
{bookingData.service === 'help' ? 'Antal timmar (per person)' : 'Bostadsyta (kvm)'}
</label>
{bookingData.service === 'help' ? (
<input type="number" value={bookingData.hours} onChange={e => handleChange('hours', e.target.value)} placeholder="T.ex. 3" style={inputStyle} />
) : (
<input type="number" value={bookingData.sqm} onChange={e => handleChange('sqm', e.target.value)} placeholder="T.ex. 85" style={inputStyle} />
)}
</div>
)}

{bookingData.service === 'minipaket' && (
<div className="mb-6">
<label className="block text-sm font-bold mb-2">Bostadsyta (kvm)</label>
<input type="number" value={bookingData.sqm}
onChange={e => handleChange('sqm', e.target.value)}
placeholder="T.ex. 75" style={inputStyle} />
{bookingData.sqm && parseInt(bookingData.sqm) > 0 && (
<p className="text-xs mt-2" style={{ color: '#888' }}>
Djupstäd varannan vecka + Standardstäd varannan vecka — 135 kr/m²
</p>
)}
</div>
)}

{bookingData.service && (hasValidInput() || bookingData.service === 'window') && bookingData.price >= 0 && (
<div className="p-4 rounded-xl mb-6" style={{ backgroundColor: '#1a1a2e' }}>
<div className={`grid gap-4 text-center ${hasRut(bookingData.service) ? 'grid-cols-3' : 'grid-cols-1'}`}>
<div>
<p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>Pris</p>
<p className="font-bold" style={{ color: hasRut(bookingData.service) ? 'rgba(255,255,255,0.4)' : '#C9A84C', textDecoration: hasRut(bookingData.service) ? 'line-through' : 'none' }}>
{bookingData.price > 0 ? `${Math.round(bookingData.price).toLocaleString()} kr` : '—'}
</p>
</div>
{hasRut(bookingData.service) && (
<>
<div>
<p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>RUT 50%</p>
<p className="font-bold" style={{ color: '#4ade80' }}>
{bookingData.rutDiscount > 0 ? `-${Math.round(bookingData.rutDiscount).toLocaleString()} kr` : '—'}
</p>
</div>
<div>
<p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>Du betalar</p>
<p className="text-xl font-bold" style={{ color: '#C9A84C' }}>
{bookingData.finalPrice > 0 ? `${Math.round(bookingData.finalPrice).toLocaleString()} kr` : '—'}
</p>
</div>
</>
)}
</div>
{bookingData.service === 'package' && (
<p className="text-center text-xs mt-2" style={{ color: 'rgba(255,255,255,0.5)' }}>per månad • du sparar ~2 000 kr vs separat bokning</p>
)}
{bookingData.service === 'window' && (
<p className="text-center text-xs mt-2" style={{ color: 'rgba(255,255,255,0.5)' }}>Startpris — uppdateras när du väljer fönster</p>
)}
</div>
)}

<button onClick={() => {
if (!bookingData.service) { alert('Välj en tjänst'); return; }
if (!hasValidInput() && bookingData.service !== 'window' && bookingData.service !== 'package') { alert('Ange bostadsyta eller timmar'); return; }
if (bookingData.service === 'package') { setPackagePhase('deep'); goToStep(2); return; }
if (bookingData.service === 'minipaket') { goToStep(3); return; }
if (noSubscriptionServices.includes(bookingData.service)) { goToStep(3); return; }
goToStep(2);
}}
className="w-full py-4 rounded-full font-bold flex items-center justify-center gap-2"
style={{ backgroundColor: bookingData.service ? '#1a1a2e' : '#e8e4d9', color: bookingData.service ? '#C9A84C' : '#aaa' }}>
Fortsätt <ArrowRight className="w-5 h-5" />
</button>
</div>
)}

{/* ── STEP 2: Subscription (non-package) ───────────────────────── */}
{step === 2 && bookingData.service !== 'package' && (
<div style={cardStyle}>
<h2 className="text-2xl font-bold mb-2">Välj Abonnemang</h2>
<p className="text-sm mb-6" style={{ color: '#888' }}>Hur ofta vill du ha städning?</p>
<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
{[
{ id: 'once', label: 'Engångsstädning', sub: 'Ingen bindningstid', discount: null },
{ id: 'weekly', label: 'Varje vecka', sub: 'Spara 15% per städning', discount: '-15%' },
{ id: 'biweekly', label: 'Varannan vecka', sub: 'Spara 10% per städning', discount: '-10%' },
{ id: 'triweekly', label: 'Var tredje vecka', sub: 'Spara 5% per städning', discount: '-5%' }
].map(sub => (
<button key={sub.id} onClick={() => handleChange('subscription', sub.id)}
className="p-4 rounded-xl text-left"
style={{ border: `2px solid ${bookingData.subscription === sub.id ? '#C9A84C' : '#e8e4d9'}`, backgroundColor: bookingData.subscription === sub.id ? 'rgba(201,168,76,0.08)' : '#f8f7f4' }}>
<div className="flex justify-between items-start">
<div>
<p className="font-bold text-sm">{sub.label}</p>
<p className="text-xs mt-0.5" style={{ color: '#888' }}>{sub.sub}</p>
</div>
{sub.discount && <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#dcfce7', color: '#16a34a' }}>{sub.discount}</span>}
</div>
{bookingData.subscription === sub.id && sub.discount && (
<div className="mt-3 space-y-1">
<p className="text-xs font-semibold" style={{ color: '#16a34a' }}>✅ Städmedel ingår</p>
<p className="text-xs font-semibold" style={{ color: '#16a34a' }}>✅ Samma städare varje gång</p>
<p className="text-xs font-semibold" style={{ color: '#16a34a' }}>✅ Spara {sub.discount} per städning</p>
</div>
)}
</button>
))}
</div>
{bookingData.price > 0 && (
<div className="p-4 rounded-xl mb-6" style={{ backgroundColor: '#1a1a2e' }}>
<div className="flex justify-between text-sm">
<span style={{ color: 'rgba(255,255,255,0.6)' }}>Pris efter abonnemang (efter RUT)</span>
<span className="font-bold" style={{ color: '#C9A84C' }}>{Math.round(bookingData.finalPrice).toLocaleString()} kr</span>
</div>
</div>
)}
<div className="flex gap-3">
<button onClick={() => goToStep(1)} className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2" style={{ backgroundColor: '#f8f7f4', color: '#555', border: '2px solid #e8e4d9' }}>
<ArrowLeft className="w-5 h-5" /> Tillbaka
</button>
<button onClick={() => goToStep(3)} className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2" style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>
Fortsätt <ArrowRight className="w-5 h-5" />
</button>
</div>
</div>
)}

{/* ── STEP 2: Package — deep addons ─────────────────────────────── */}
{step === 2 && bookingData.service === 'package' && packagePhase === 'deep' && (
<div style={cardStyle}>
<div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: '#1a1a2e' }}>
<p className="font-bold" style={{ color: '#C9A84C' }}>⭐ Månadspaket — Vecka 1</p>
<p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>Anpassa din djupstädning</p>
</div>
<h2 className="text-xl font-bold mb-6">Tillägg till Djupstädning</h2>
<div className="mb-6 p-5 rounded-2xl" style={{ border: '1px solid #e8e4d9', backgroundColor: '#f8f7f4' }}>
<label className="block font-bold mb-2">Bostadsyta (kvm)</label>
<p className="text-xs mb-3" style={{ color: '#888' }}>Vi anpassar tid och antal personal baserat på storleken</p>
<input type="number" value={bookingData.sqm}
onChange={e => handleChange('sqm', e.target.value)}
placeholder="T.ex. 85"
style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '2px solid #e8e4d9', backgroundColor: '#fff', color: '#1a1a2e', outline: 'none', fontSize: '16px' }} />
</div>
<div className="mb-6 p-5 rounded-2xl" style={{ border: '1px solid #e8e4d9', backgroundColor: '#f8f7f4' }}>
<div className="flex justify-between items-center">
<div><p className="font-bold">Ugnsrengöring</p><p className="text-xs" style={{ color: '#888' }}>Grundlig rengöring av ugn</p></div>
<Counter value={bookingData.packageDeepAddons.ovenCount} onChange={v => updatePackageDeep('ovenCount', v)} />
</div>
</div>
<div className="mb-6 p-5 rounded-2xl" style={{ border: '1px solid #e8e4d9', backgroundColor: '#f8f7f4' }}>
<div className="flex justify-between items-center">
<div><p className="font-bold">Kylskåpsrengöring</p><p className="text-xs" style={{ color: '#888' }}>Grundlig rengöring av kylskåp</p></div>
<Counter value={bookingData.packageDeepAddons.fridgeCount} onChange={v => updatePackageDeep('fridgeCount', v)} />
</div>
</div>
<div className="mb-6 p-5 rounded-2xl" style={{ border: '1px solid #e8e4d9', backgroundColor: '#f8f7f4' }}>
<p className="font-bold mb-3">Fönsterputs till djupstädningen?</p>
<div className="flex gap-3">
{[{ val: true, label: 'Ja' }, { val: false, label: 'Nej' }].map(o => (
<button key={String(o.val)} onClick={() => updatePackageDeep('windowCleaning', o.val)}
className="flex-1 py-3 rounded-xl font-semibold text-sm"
style={{ border: `2px solid ${bookingData.packageDeepAddons.windowCleaning === o.val ? '#C9A84C' : '#e8e4d9'}`, backgroundColor: bookingData.packageDeepAddons.windowCleaning === o.val ? 'rgba(201,168,76,0.08)' : '#fff' }}>
{o.label}
</button>
))}
</div>
</div>
<WhatIsIncluded serviceKey="deep" />
<div className="flex gap-3 mt-6">
<button onClick={() => goToStep(1)} className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2" style={{ backgroundColor: '#f8f7f4', color: '#555', border: '2px solid #e8e4d9' }}>
<ArrowLeft className="w-5 h-5" /> Tillbaka
</button>
<button onClick={() => {
if (!bookingData.sqm || parseInt(bookingData.sqm) <= 0) { alert('Ange bostadsyta (kvm)'); return; }
if (bookingData.packageDeepAddons.windowCleaning) {
changePackagePhase('deepWindow');
} else {
changePackagePhase('standard');
}
}} className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2" style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>
Fortsätt <ArrowRight className="w-5 h-5" />
</button>
</div>
</div>
)}

{/* ── STEP 2: Package — deep window ─────────────────────────────── */}
{step === 2 && bookingData.service === 'package' && packagePhase === 'deepWindow' && (
<div style={cardStyle}>
<div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: '#1a1a2e' }}>
<p className="font-bold" style={{ color: '#C9A84C' }}>⭐ Månadspaket — Vecka 1 — Fönsterputs</p>
<p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>Välj fönster för djupstädningen</p>
</div>
<h2 className="text-xl font-bold mb-6">Fönsterdetaljer — Djupstädning</h2>
<div className="space-y-4 mb-8">
{Object.entries(bookingData.packageDeepAddons.windows).map(([type, data]) => (
<div key={type} className="p-5 rounded-2xl" style={{ border: '1px solid #e8e4d9', backgroundColor: '#f8f7f4' }}>
<div className="flex justify-between items-center mb-3">
<p className="font-bold">{data.label}</p>
<Counter value={data.count || 0} onChange={v => {
const newWindows = { ...bookingData.packageDeepAddons.windows, [type]: { ...data, count: v } };
setBookingData(recalc({ ...bookingData, packageDeepAddons: { ...bookingData.packageDeepAddons, windows: newWindows } }));
}} />
</div>
{(data.count || 0) > 0 && (
<div className="pt-3" style={{ borderTop: '1px solid #e8e4d9' }}>
<p className="text-xs font-bold mb-2" style={{ color: '#888' }}>Välj antal sidor:</p>
<div className="flex gap-2">
{[{ id: 'double', label: 'Dubbelsidigt' }, { id: 'single', label: 'Enkelsidigt' }].map(side => (
<button key={side.id}
onClick={() => setBookingData(prev => recalc({ ...prev, windowSides: { ...prev.windowSides, [`deep_${type}`]: side.id } }))}
className="flex-1 py-2 rounded-xl text-xs font-semibold"
style={{ border: `2px solid ${(bookingData.windowSides?.[`deep_${type}`] || 'double') === side.id ? '#C9A84C' : '#e8e4d9'}`, backgroundColor: (bookingData.windowSides?.[`deep_${type}`] || 'double') === side.id ? 'rgba(201,168,76,0.08)' : '#fff' }}>
{side.label}
</button>
))}
</div>
</div>
)}
</div>
))}
</div>
<div className="space-y-3 mb-8">
{[
{ key: 'karmtvätt', label: 'Karmtvätt', desc: 'Tvätt av fönstrets bågar och karmar' },
{ key: 'stege', label: 'Stege behövs', desc: 'Vi tar med stege för svåråtkomliga fönster' }
].map(addon => (
<button key={addon.key} onClick={() => updatePackageDeep(addon.key, !bookingData.packageDeepAddons[addon.key])}
className="w-full p-4 rounded-xl text-left"
style={{ border: `2px solid ${bookingData.packageDeepAddons[addon.key] ? '#C9A84C' : '#e8e4d9'}`, backgroundColor: bookingData.packageDeepAddons[addon.key] ? 'rgba(201,168,76,0.08)' : '#f8f7f4' }}>
<p className="font-semibold text-sm">{bookingData.packageDeepAddons[addon.key] ? '✅ ' : '○ '}{addon.label}</p>
<p className="text-xs mt-0.5" style={{ color: '#888' }}>{addon.desc}</p>
</button>
))}
</div>
<div className="flex gap-3">
<button onClick={() => changePackagePhase('deep')} className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2" style={{ backgroundColor: '#f8f7f4', color: '#555', border: '2px solid #e8e4d9' }}>
<ArrowLeft className="w-5 h-5" /> Tillbaka
</button>
<button onClick={() => changePackagePhase('standard')} className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2" style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>
Fortsätt <ArrowRight className="w-5 h-5" />
</button>
</div>
</div>
)}

{/* ── STEP 2: Package — standard addons ─────────────────────────── */}
{step === 2 && bookingData.service === 'package' && packagePhase === 'standard' && (
<div style={cardStyle}>
<div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: '#1a1a2e' }}>
<p className="font-bold" style={{ color: '#C9A84C' }}>⭐ Månadspaket — Vecka 2, 3 & 4</p>
<p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>Anpassa dina standardstädningar</p>
</div>
<h2 className="text-xl font-bold mb-2">Tillägg till Standardstädning (×3)</h2>
<p className="text-sm mb-6" style={{ color: '#888' }}>Dessa tillägg gäller för alla tre standardstädningarna</p>
<div className="mb-6 p-5 rounded-2xl" style={{ border: '1px solid #e8e4d9', backgroundColor: '#f8f7f4' }}>
<div className="flex justify-between items-center">
<div><p className="font-bold">Ugnsrengöring</p><p className="text-xs" style={{ color: '#888' }}>Gäller ×3 städningar</p></div>
<Counter value={bookingData.packageStandardAddons.ovenCount} onChange={v => updatePackageStandard('ovenCount', v)} />
</div>
</div>
<div className="mb-6 p-5 rounded-2xl" style={{ border: '1px solid #e8e4d9', backgroundColor: '#f8f7f4' }}>
<div className="flex justify-between items-center">
<div><p className="font-bold">Kylskåpsrengöring</p><p className="text-xs" style={{ color: '#888' }}>Gäller ×3 städningar</p></div>
<Counter value={bookingData.packageStandardAddons.fridgeCount} onChange={v => updatePackageStandard('fridgeCount', v)} />
</div>
</div>
<div className="mb-6 p-5 rounded-2xl" style={{ border: '1px solid #e8e4d9', backgroundColor: '#f8f7f4' }}>
<p className="font-bold mb-3">Fönsterputs till standardstädningarna?</p>
<div className="flex gap-3">
{[{ val: true, label: 'Ja' }, { val: false, label: 'Nej' }].map(o => (
<button key={String(o.val)} onClick={() => updatePackageStandard('windowCleaning', o.val)}
className="flex-1 py-3 rounded-xl font-semibold text-sm"
style={{ border: `2px solid ${bookingData.packageStandardAddons.windowCleaning === o.val ? '#C9A84C' : '#e8e4d9'}`, backgroundColor: bookingData.packageStandardAddons.windowCleaning === o.val ? 'rgba(201,168,76,0.08)' : '#fff' }}>
{o.label}
</button>
))}
</div>
</div>
<div className="p-4 rounded-xl mb-4" style={{ backgroundColor: '#1a1a2e' }}>
<div className="grid grid-cols-3 gap-4 text-center">
<div>
<p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>Månadspris</p>
<p className="font-bold line-through text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>{Math.round(bookingData.price).toLocaleString()} kr</p>
</div>
<div>
<p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>RUT 50%</p>
<p className="font-bold" style={{ color: '#4ade80' }}>-{Math.round(bookingData.rutDiscount).toLocaleString()} kr</p>
</div>
<div>
<p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>Du betalar</p>
<p className="text-xl font-bold" style={{ color: '#C9A84C' }}>{Math.round(bookingData.finalPrice).toLocaleString()} kr/mån</p>
</div>
</div>
</div>
<WhatIsIncluded serviceKey="standard" />
<div className="flex gap-3 mt-6">
<button onClick={() => changePackagePhase(bookingData.packageDeepAddons.windowCleaning ? 'deepWindow' : 'deep')}
className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2" style={{ backgroundColor: '#f8f7f4', color: '#555', border: '2px solid #e8e4d9' }}>
<ArrowLeft className="w-5 h-5" /> Tillbaka
</button>
<button onClick={() => {
if (bookingData.packageStandardAddons.windowCleaning) {
changePackagePhase('standardWindow');
} else {
goToStep(3);
}
}} className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2" style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>
Fortsätt <ArrowRight className="w-5 h-5" />
</button>
</div>
</div>
)}

{/* ── STEP 2: Package — standard window ─────────────────────────── */}
{step === 2 && bookingData.service === 'package' && packagePhase === 'standardWindow' && (
<div style={cardStyle}>
<div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: '#1a1a2e' }}>
<p className="font-bold" style={{ color: '#C9A84C' }}>⭐ Månadspaket — Vecka 2, 3 & 4 — Fönsterputs</p>
<p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>Välj fönster för standardstädningarna (×3)</p>
</div>
<h2 className="text-xl font-bold mb-6">Fönsterdetaljer — Standardstädning (×3)</h2>
<div className="space-y-4 mb-8">
{Object.entries(initWindows()).map(([type, defaultData]) => {
const data = bookingData.packageStandardAddons.windows?.[type] || defaultData;
return (
<div key={type} className="p-5 rounded-2xl" style={{ border: '1px solid #e8e4d9', backgroundColor: '#f8f7f4' }}>
<div className="flex justify-between items-center mb-3">
<p className="font-bold">{defaultData.label}</p>
<Counter value={data.count || 0} onChange={v => {
const newWindows = { ...(bookingData.packageStandardAddons.windows || initWindows()), [type]: { ...defaultData, ...data, count: v } };
updatePackageStandard('windows', newWindows);
}} />
</div>
{(data.count || 0) > 0 && (
<div className="pt-3" style={{ borderTop: '1px solid #e8e4d9' }}>
<p className="text-xs font-bold mb-2" style={{ color: '#888' }}>Välj antal sidor:</p>
<div className="flex gap-2">
{[{ id: 'double', label: 'Dubbelsidigt' }, { id: 'single', label: 'Enkelsidigt' }].map(side => (
<button key={side.id}
onClick={() => setBookingData(prev => recalc({ ...prev, windowSides: { ...prev.windowSides, [`std_${type}`]: side.id } }))}
className="flex-1 py-2 rounded-xl text-xs font-semibold"
style={{ border: `2px solid ${(bookingData.windowSides?.[`std_${type}`] || 'double') === side.id ? '#C9A84C' : '#e8e4d9'}`, backgroundColor: (bookingData.windowSides?.[`std_${type}`] || 'double') === side.id ? 'rgba(201,168,76,0.08)' : '#fff' }}>
{side.label}
</button>
))}
</div>
</div>
)}
</div>
);
})}
</div>
<div className="space-y-3 mb-8">
{[
{ key: 'karmtvätt', label: 'Karmtvätt (×3)', desc: 'Tvätt av fönstrets bågar och karmar' },
{ key: 'stege', label: 'Stege behövs', desc: 'Vi tar med stege för svåråtkomliga fönster' }
].map(addon => (
<button key={addon.key} onClick={() => updatePackageStandard(addon.key, !bookingData.packageStandardAddons[addon.key])}
className="w-full p-4 rounded-xl text-left"
style={{ border: `2px solid ${bookingData.packageStandardAddons[addon.key] ? '#C9A84C' : '#e8e4d9'}`, backgroundColor: bookingData.packageStandardAddons[addon.key] ? 'rgba(201,168,76,0.08)' : '#f8f7f4' }}>
<p className="font-semibold text-sm">{bookingData.packageStandardAddons[addon.key] ? '✅ ' : '○ '}{addon.label}</p>
<p className="text-xs mt-0.5" style={{ color: '#888' }}>{addon.desc}</p>
</button>
))}
</div>
<div className="flex gap-3">
<button onClick={() => changePackagePhase('standard')} className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2" style={{ backgroundColor: '#f8f7f4', color: '#555', border: '2px solid #e8e4d9' }}>
<ArrowLeft className="w-5 h-5" /> Tillbaka
</button>
<button onClick={() => goToStep(3)} className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2" style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>
Fortsätt till Tillägg <ArrowRight className="w-5 h-5" />
</button>
</div>
</div>
)}

{/* ── STEP 3: Addons ────────────────────────────────────────────── */}
{step === 3 && (
<div style={cardStyle}>
<h2 className="text-2xl font-bold mb-2">Tilläggstjänster</h2>
<p className="text-sm mb-8" style={{ color: '#888' }}>Anpassa din städning</p>

{/* Equipment — not for window or help */}
{bookingData.service !== 'window' && bookingData.service !== 'help' && (
<div className="mb-6 p-5 rounded-2xl"
style={{ border: `2px solid ${bookingData.needsEquipment ? '#C9A84C' : '#e8e4d9'}`, backgroundColor: bookingData.needsEquipment ? 'rgba(201,168,76,0.05)' : '#f8f7f4' }}>
<label className="flex items-start gap-3 cursor-pointer">
<input type="checkbox" checked={bookingData.needsEquipment} onChange={e => handleChange('needsEquipment', e.target.checked)} className="mt-1 w-5 h-5 flex-shrink-0" />
<div>
<p className="font-bold">Bostaden saknar städredskap</p>
<p className="text-sm mt-1" style={{ color: '#555' }}>
För att vi ska kunna städa behöver du ha dammsugare (ej portabel), mopp och hink. Saknar du detta kan vi ta med det mot en tilläggskostnad.
</p>
</div>
</label>
</div>
)}

{/* Window — not for help, moving, window, OR package (already asked) */}
{bookingData.service !== 'window' && bookingData.service !== 'help' && bookingData.service !== 'moving' && bookingData.service !== 'package' && (
<div className="mb-6 p-5 rounded-2xl" style={{ border: '1px solid #e8e4d9', backgroundColor: '#f8f7f4' }}>
<p className="font-bold mb-3">Vill du lägga till fönsterputs?</p>
<div className="flex gap-3">
{[{ val: true, label: 'Ja, lägg till' }, { val: false, label: 'Nej tack' }].map(o => (
<button key={String(o.val)} onClick={() => handleChange('windowCleaning', o.val)}
className="flex-1 py-3 rounded-xl font-semibold text-sm"
style={{ border: `2px solid ${bookingData.windowCleaning === o.val ? '#C9A84C' : '#e8e4d9'}`, backgroundColor: bookingData.windowCleaning === o.val ? 'rgba(201,168,76,0.08)' : '#fff' }}>
{o.label}
</button>
))}
</div>
</div>
)}

{/* Oven & fridge — not for help, window, OR package (already asked) */}
{bookingData.service !== 'help' && bookingData.service !== 'window' && bookingData.service !== 'package' && (
<>
<div className="mb-6 p-5 rounded-2xl" style={{ border: '1px solid #e8e4d9', backgroundColor: '#f8f7f4' }}>
<div className="flex justify-between items-center">
<div><p className="font-bold">Ugnsrengöring</p><p className="text-xs" style={{ color: '#888' }}>Grundlig rengöring av ugn</p></div>
<Counter value={bookingData.ovenCount} onChange={v => handleChange('ovenCount', v)} />
</div>
</div>
<div className="mb-8 p-5 rounded-2xl" style={{ border: '1px solid #e8e4d9', backgroundColor: '#f8f7f4' }}>
<div className="flex justify-between items-center">
<div><p className="font-bold">Kylskåpsrengöring</p><p className="text-xs" style={{ color: '#888' }}>Grundlig rengöring av kylskåp</p></div>
<Counter value={bookingData.fridgeCount} onChange={v => handleChange('fridgeCount', v)} />
</div>
</div>
</>
)}

{/* Pets — not for help or window */}
{bookingData.service !== 'help' && bookingData.service !== 'window' && (
<div className="mb-8">
<h3 className="font-bold mb-1">
Om din bostad
<InfoTooltip title="Varför behöver vi veta om husdjur?" content="Vi behöver veta om du har husdjur för att förbereda rätt utrustning. Husdjurshår kräver specifika tillbehör och vi vill inte orsaka stress för ditt djur under städningen." />
</h3>
<p className="text-sm font-semibold mb-3" style={{ color: '#555' }}>Har ni husdjur hemma?</p>
<div className="flex gap-3 mb-4">
{[{ val: true, label: 'Ja' }, { val: false, label: 'Nej' }].map(o => (
<button key={String(o.val)} onClick={() => handleChange('hasPets', o.val)}
className="flex-1 py-3 rounded-xl font-semibold text-sm"
style={{ border: `2px solid ${bookingData.hasPets === o.val ? '#C9A84C' : '#e8e4d9'}`, backgroundColor: bookingData.hasPets === o.val ? 'rgba(201,168,76,0.08)' : '#f8f7f4' }}>
{o.label}
</button>
))}
</div>
{bookingData.hasPets && (
<div className="flex gap-2 flex-wrap">
{['Katt', 'Hund', 'Annat husdjur'].map(pet => {
const sel = bookingData.petTypes.includes(pet);
return (
<button key={pet} onClick={() => handleChange('petTypes', sel ? bookingData.petTypes.filter(p => p !== pet) : [...bookingData.petTypes, pet])}
className="px-4 py-2 rounded-full font-semibold text-sm"
style={{ border: `2px solid ${sel ? '#C9A84C' : '#e8e4d9'}`, backgroundColor: sel ? 'rgba(201,168,76,0.08)' : '#f8f7f4' }}>
{pet}
</button>
);
})}
</div>
)}
</div>
)}

{/* Extra notes */}
<div className="mb-8">
<div className="flex items-center gap-2 mb-2">
<h3 className="font-bold">Är det något mer du behöver hjälp med?</h3>
{bookingData.service !== 'window' && (
<InfoTooltip title="Tilläggstjänster" content="Vi kan hjälpa dig med: Invändig rengöring av köksskåp och lådor, polering av kristallkronor, putsning av mässing eller silver, strykning, byte av sängkläder. Vi återkommer med pris." />
)}
</div>
<textarea value={bookingData.extraNotes} onChange={e => handleChange('extraNotes', e.target.value)}
placeholder={bookingData.service === 'window' ? 'Specialförhållanden kring dina fönster...' : 'Berätta vad du behöver hjälp med...'}
rows={3} style={{ ...inputStyle, resize: 'none' }} />
</div>

{/* Key handling */}
<div className="mb-8">
<h3 className="font-bold mb-3">Nyckelhantering</h3>
<div className="space-y-2">
{[
{ id: 'home', label: 'Jag är hemma och öppnar' },
{ id: 'dropoff', label: 'Jag lämnar nyckeln på ert kontor senast kl. 12 minst en arbetsdag innan' },
{ id: 'already', label: 'Jag har redan lämnat mina nycklar' }
].map(k => (
<button key={k.id} onClick={() => handleChange('keyHandling', k.id)}
className="w-full p-4 rounded-xl text-left font-semibold text-sm"
style={{ border: `2px solid ${bookingData.keyHandling === k.id ? '#C9A84C' : '#e8e4d9'}`, backgroundColor: bookingData.keyHandling === k.id ? 'rgba(201,168,76,0.08)' : '#f8f7f4' }}>
{bookingData.keyHandling === k.id ? '✅ ' : '○ '}{k.label}
</button>
))}
</div>
</div>

<div className="flex gap-3">
<button onClick={() => {
if (bookingData.service === 'package') {
if (bookingData.packageStandardAddons.windowCleaning) { changePackagePhase('standardWindow'); goToStep(2); }
else { changePackagePhase('standard'); goToStep(2); }
} else if (noSubscriptionServices.includes(bookingData.service)) {
goToStep(1);
} else {
goToStep(2);
}
}} className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2" style={{ backgroundColor: '#f8f7f4', color: '#555', border: '2px solid #e8e4d9' }}>
<ArrowLeft className="w-5 h-5" /> Tillbaka
</button>
<button onClick={() => goToStep(bookingData.windowCleaning || bookingData.service === 'window' ? 'W' : 4)}
className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2"
style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>
Fortsätt <ArrowRight className="w-5 h-5" />
</button>
</div>

<WhatIsIncluded serviceKey={bookingData.service} />
</div>
)}

{/* ── STEP W: Window details ─────────────────────────────────────── */}
{step === 'W' && (
<div style={cardStyle}>
<h2 className="text-2xl font-bold mb-2">Fönsterputs</h2>
<p className="text-sm mb-6" style={{ color: '#888' }}>Ange antalet fönster och typ</p>
<div className="space-y-4 mb-8">
{Object.entries(bookingData.windows).map(([type, data]) => (
<div key={type} className="p-5 rounded-2xl" style={{ border: '1px solid #e8e4d9', backgroundColor: '#f8f7f4' }}>
<div className="flex justify-between items-center mb-3">
<p className="font-bold">{data.label}</p>
<Counter value={data.count} onChange={v => updateWindow(type, 'count', v)} />
</div>
{data.count > 0 && (
<div className="pt-3" style={{ borderTop: '1px solid #e8e4d9' }}>
<p className="text-xs font-bold mb-2" style={{ color: '#888' }}>Välj antal sidor:</p>
<div className="flex gap-2">
{[{ id: 'double', label: 'Dubbelsidigt (båda sidor)' }, { id: 'single', label: 'Enkelsidigt (en sida)' }].map(side => (
<button key={side.id} onClick={() => updateWindowSide(type, side.id)}
className="flex-1 py-2 rounded-xl text-xs font-semibold"
style={{ border: `2px solid ${(bookingData.windowSides[type] || 'double') === side.id ? '#C9A84C' : '#e8e4d9'}`, backgroundColor: (bookingData.windowSides[type] || 'double') === side.id ? 'rgba(201,168,76,0.08)' : '#fff' }}>
{side.label}
</button>
))}
</div>
{type === 'balkong' && (
<div className="mt-2 flex gap-2">
{['Helt inglasad', 'Endast glasräcke'].map(bt => (
<button key={bt} onClick={() => setBookingData(prev => ({ ...prev, balkongType: { ...prev.balkongType, balkong: bt } }))}
className="flex-1 py-2 rounded-xl text-xs font-semibold"
style={{ border: `2px solid ${bookingData.balkongType?.balkong === bt ? '#C9A84C' : '#e8e4d9'}`, backgroundColor: bookingData.balkongType?.balkong === bt ? 'rgba(201,168,76,0.08)' : '#fff' }}>
{bt}
</button>
))}
</div>
)}
</div>
)}
</div>
))}
</div>
<h3 className="font-bold mb-4">Tillägg för fönsterputs</h3>
<div className="space-y-3 mb-8">
{[
{ key: 'karmtvätt', label: 'Karmtvätt', desc: 'Tvätt av fönstrets bågar och karmar — rekommenderas' },
{ key: 'stege', label: 'Stege behövs', desc: 'Vi tar med stege för svåråtkomliga fönster' }
].map(addon => (
<button key={addon.key} onClick={() => handleChange(addon.key, !bookingData[addon.key])}
className="w-full p-4 rounded-xl text-left"
style={{ border: `2px solid ${bookingData[addon.key] ? '#C9A84C' : '#e8e4d9'}`, backgroundColor: bookingData[addon.key] ? 'rgba(201,168,76,0.08)' : '#f8f7f4' }}>
<p className="font-semibold text-sm">{bookingData[addon.key] ? '✅ ' : '○ '}{addon.label}</p>
<p className="text-xs mt-0.5" style={{ color: '#888' }}>{addon.desc}</p>
</button>
))}
</div>
<div className="mb-8">
<label className="block font-semibold text-sm mb-2">Är det något mer vi behöver veta?</label>
<textarea value={bookingData.notes} onChange={e => handleChange('notes', e.target.value)}
placeholder="Specialförhållanden eller annat viktigt..." rows={3} style={{ ...inputStyle, resize: 'none' }} />
</div>
<div className="flex gap-3">
<button onClick={() => goToStep(3)} className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2" style={{ backgroundColor: '#f8f7f4', color: '#555', border: '2px solid #e8e4d9' }}>
<ArrowLeft className="w-5 h-5" /> Tillbaka
</button>
<button onClick={() => goToStep(4)} className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2" style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>
Fortsätt <ArrowRight className="w-5 h-5" />
</button>
</div>
<div style={sectionDivider}>
<h3 className="text-lg font-bold mb-4">Vad ingår i fönsterputs?</h3>
<FAQ faqs={SERVICE_CONTENT.window.faqs} />
</div>
</div>
)}

{/* ── STEP 4: Date & Time ───────────────────────────────────────── */}
{step === 4 && (
<div style={cardStyle}>
<h2 className="text-2xl font-bold mb-2">Välj Datum & Tid</h2>
<p className="text-sm mb-6" style={{ color: '#888' }}>När vill du att vi kommer?</p>
<div className="mb-6">
<label className="block text-sm font-bold mb-3"><Calendar className="w-4 h-4 inline mr-1" />Välj Dag</label>
<div className="flex items-center justify-between mb-4">
<button onClick={() => { const w = parseInt(bookingData.currentWeek || 0); if (w > 0) handleChange('currentWeek', w - 1); }}
disabled={!bookingData.currentWeek || bookingData.currentWeek === 0}
className="px-4 py-2 rounded-full font-semibold text-sm flex items-center gap-2"
style={{ backgroundColor: '#f8f7f4', color: '#555', border: '2px solid #e8e4d9', opacity: !bookingData.currentWeek || bookingData.currentWeek === 0 ? 0.4 : 1 }}>
<ArrowLeft className="w-4 h-4" /> Föregående
</button>
<span className="text-sm font-semibold" style={{ color: '#888' }}>Vecka {(parseInt(bookingData.currentWeek || 0) + 1)}</span>
<button onClick={() => { const w = parseInt(bookingData.currentWeek || 0); handleChange('currentWeek', w + 1); }}
className="px-4 py-2 rounded-full font-semibold text-sm flex items-center gap-2"
style={{ backgroundColor: '#f8f7f4', color: '#555', border: '2px solid #e8e4d9' }}>
Nästa <ArrowRight className="w-4 h-4" />
</button>
</div>
<div className="grid grid-cols-5 gap-2">
{(() => {
const days = [];
const today = new Date();
const cw = parseInt(bookingData.currentWeek || 0);
for (let i = 0; i < 5; i++) {
const date = new Date(today);
date.setDate(today.getDate() + cw * 5 + i);
const dateStr = localDateStr(date);
const dayName = ['Sön','Mån','Tis','Ons','Tor','Fre','Lör'][date.getDay()];
const dayNum = date.getDate();
const monthName = ['Jan','Feb','Mar','Apr','Maj','Jun','Jul','Aug','Sep','Okt','Nov','Dec'][date.getMonth()];
const blocked = isDateBlocked(dateStr);
const selected = bookingData.date === dateStr;
const isSoon = isWithin48h(dateStr);
const isToday = i === 0 && cw === 0;
days.push(
<button key={i} onClick={() => !blocked && handleChange('date', dateStr)} disabled={blocked}
className="p-2 rounded-xl text-center transition-all"
style={{ border: `2px solid ${blocked ? '#fecaca' : selected ? '#C9A84C' : isSoon ? '#fde68a' : '#e8e4d9'}`, backgroundColor: blocked ? '#fef2f2' : selected ? 'rgba(201,168,76,0.1)' : isSoon ? '#fef9ec' : '#f8f7f4', cursor: blocked ? 'not-allowed' : 'pointer' }}>
<div className="text-xs mb-1" style={{ color: blocked ? '#dc2626' : isSoon ? '#92400e' : '#888' }}>{dayName}</div>
<div className="font-bold text-lg" style={{ color: blocked ? '#dc2626' : '#1a1a2e' }}>{dayNum}</div>
<div className="text-xs" style={{ color: '#888' }}>{monthName}</div>
{blocked && <div className="text-xs mt-1" style={{ color: '#dc2626' }}>Stängt</div>}
{isSoon && !blocked && <div className="text-xs mt-1" style={{ color: '#92400e' }}>{isToday ? 'Samma dag' : 'Kort varsel'}</div>}
</button>
);
}
return days;
})()}
</div>
{dateWarning && (
<div className="mt-3 p-3 rounded-xl" style={{ backgroundColor: '#fef9ec', border: '1px solid #fde68a' }}>
<p className="text-xs font-semibold" style={{ color: '#92400e' }}>{dateWarning}</p>
</div>
)}
</div>
{bookingData.date && (
<div className="mb-6">
<label className="block text-sm font-bold mb-3"><Clock className="w-4 h-4 inline mr-1" />Välj Tid</label>
<div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
{availableTimes.map(time => {
const tooSoon = isTodayOrPast(bookingData.date);
  const booked = isTimeBooked(bookingData.date, time);
  const selected = bookingData.time === time;
  const off = booked || tooSoon;
  return (
    <button key={time} onClick={() => !off && handleChange('time', time)} disabled={off}
      className="p-3 rounded-xl font-semibold text-sm"
      style={{
        border: `2px solid ${booked ? '#fecaca' : tooSoon ? '#C9A84C' : selected ? '#C9A84C' : '#e8e4d9'}`,
        backgroundColor: booked ? '#fef2f2' : tooSoon ? 'rgba(201,168,76,0.12)' : selected ? 'rgba(201,168,76,0.1)' : '#f8f7f4',
        color: booked ? '#dc2626' : tooSoon ? '#a8841f' : '#1a1a2e',
        textDecoration: off ? 'line-through' : 'none',
        cursor: off ? 'not-allowed' : 'pointer'
      }}>
      {time}
    </button>
  );
})}
</div>
</div>
)}
{!bookingData.date && <p className="text-center text-sm mb-6" style={{ color: '#aaa' }}>Välj ett datum för att se tider</p>}
<div className="flex gap-3">
<button onClick={() => {
if (bookingData.windowCleaning || bookingData.service === 'window') goToStep('W');
else goToStep(3);
}} className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2" style={{ backgroundColor: '#f8f7f4', color: '#555', border: '2px solid #e8e4d9' }}>
<ArrowLeft className="w-5 h-5" /> Tillbaka
</button>
<button onClick={() => goToStep(5)} disabled={!bookingData.date || !bookingData.time}
className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2"
style={{ backgroundColor: bookingData.date && bookingData.time ? '#1a1a2e' : '#e8e4d9', color: bookingData.date && bookingData.time ? '#C9A84C' : '#aaa' }}>
Fortsätt <ArrowRight className="w-5 h-5" />
</button>
</div>
</div>
)}

{/* ── STEP 5: Personal info ─────────────────────────────────────── */}
{step === 5 && (
<div style={cardStyle}>
<h2 className="text-2xl font-bold mb-2">Dina Uppgifter</h2>
<p className="text-sm mb-6" style={{ color: '#888' }}>För fakturering och kontakt</p>
{currentUser?.role === 'customer' && (
<div className="mb-4 p-3 rounded-xl" style={{ backgroundColor: '#dcfce7', border: '1px solid #86efac' }}>
<p className="text-xs font-semibold" style={{ color: '#16a34a' }}>✅ Inloggad som {currentUser.name} — dina uppgifter är förifyllda!</p>
</div>
)}
<div className="space-y-4">
<div>
<label className="block text-sm font-bold mb-2"><User className="w-4 h-4 inline mr-1" />Fullständigt namn</label>
<input type="text" value={bookingData.name} onChange={e => handleChange('name', e.target.value)} placeholder="John Andersson" style={inputStyle} />
</div>
<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
<div>
<label className="block text-sm font-bold mb-2"><Mail className="w-4 h-4 inline mr-1" />Email</label>
<input type="email" value={bookingData.email} onChange={e => handleChange('email', e.target.value)} placeholder="john@example.com" style={inputStyle} />
</div>
<div>
<label className="block text-sm font-bold mb-2"><Phone className="w-4 h-4 inline mr-1" />Telefon</label>
<input type="tel" value={bookingData.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="070-XXX XX XX" style={inputStyle} />
</div>
</div>
<div>
<label className="block text-sm font-bold mb-2">🪪 Personnummer</label>
<input type="text" value={bookingData.personnummer} onChange={e => handleChange('personnummer', e.target.value)}
placeholder="ÅÅMMDD-XXXX" maxLength={13}
style={{ ...inputStyle, border: pnrError ? '2px solid #dc2626' : bookingData.personnummer && validatePnr(bookingData.personnummer) ? '2px solid #16a34a' : '2px solid #e8e4d9' }} />
{pnrError && <p className="text-xs mt-1" style={{ color: '#dc2626' }}>❌ {pnrError}</p>}
{bookingData.personnummer && validatePnr(bookingData.personnummer) && !pnrError && (
<p className="text-xs mt-1" style={{ color: '#16a34a' }}>✅ Giltigt personnummer</p>
)}
<p className="text-xs mt-1" style={{ color: '#888' }}>Krävs för fakturering och RUT-ansökan</p>
</div>
<div>
<label className="block text-sm font-bold mb-2"><MapPin className="w-4 h-4 inline mr-1" />Adress</label>
<input type="text" value={bookingData.address} onChange={e => handleChange('address', e.target.value)} placeholder="Exempelgatan 123" style={inputStyle} />
</div>
<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
<div>
<label className="block text-sm font-bold mb-2">Stad</label>
<input type="text" value={bookingData.city} onChange={e => handleChange('city', e.target.value)} style={inputStyle} />
</div>
<div>
<label className="block text-sm font-bold mb-2">Postnummer</label>
<input type="text" value={bookingData.postalCode} onChange={e => handleChange('postalCode', e.target.value)} placeholder="591 XX" style={inputStyle} />
{bookingData.postalCode.replace(/\s/g, '').length === 5 && !isInMotala(bookingData.postalCode) && (
  <p className="text-xs mt-1 font-semibold" style={{ color: '#dc2626' }}>❌ Tyvärr städar vi bara inom Östergötland.</p>
)}
</div>
</div>
<div>
<label className="block text-sm font-bold mb-2">Meddelande (valfritt)</label>
<textarea value={bookingData.notes} onChange={e => handleChange('notes', e.target.value)} placeholder="Speciella önskemål..." rows={3} style={{ ...inputStyle, resize: 'none' }} />
</div>
</div>
<div className="flex gap-3 mt-6">
<button onClick={() => goToStep(4)} className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2" style={{ backgroundColor: '#f8f7f4', color: '#555', border: '2px solid #e8e4d9' }}>
<ArrowLeft className="w-5 h-5" /> Tillbaka
</button>
<button onClick={() => { if (validateInfo()) handleSubmit(); }}
className="flex-1 py-4 rounded-full font-bold flex items-center justify-center gap-2"
style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>
Bekräfta Bokning <CheckCircle2 className="w-5 h-5" />
</button>
</div>
</div>
)}

{/* ── STEP 99: Confirmation ─────────────────────────────────────── */}
{step === 99 && (
<div style={{ ...cardStyle, textAlign: 'center' }}>
<div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#C9A84C' }}>
<CheckCircle2 className="w-12 h-12" style={{ color: '#1a1a2e' }} />
</div>
<h2 className="text-3xl font-bold mb-2">Bokning Bekräftad!</h2>
<p className="mb-6" style={{ color: '#888' }}>Tack {bookingData.name}! Vi återkommer med bekräftelse inom kort.</p>
<div className="p-6 rounded-2xl mb-4 text-left" style={{ backgroundColor: '#f8f7f4', border: '1px solid #e8e4d9' }}>
<h3 className="font-bold mb-3">Sammanfattning:</h3>
<div className="space-y-2 text-sm">
<p><span style={{ color: '#888' }}>Tjänst:</span> <span className="font-semibold">{serviceNames[bookingData.service]}</span></p>
{bookingData.subscription && bookingData.subscription !== 'once' && (
<p><span style={{ color: '#888' }}>Abonnemang:</span> <span className="font-semibold">
{bookingData.subscription === 'weekly' ? 'Varje vecka' : bookingData.subscription === 'biweekly' ? 'Varannan vecka' : 'Var tredje vecka'}
</span></p>
)}
<p><span style={{ color: '#888' }}>Datum:</span> <span className="font-semibold">
                    {bookingData.date} kl {bookingData.time}
                    {bookingData.date && (bookingData.service === 'package' || bookingData.service === 'minipaket' || (bookingData.subscription && bookingData.subscription !== 'once')) && (() => {
                      const days = ['söndagar','måndagar','tisdagar','onsdagar','torsdagar','fredagar','lördagar'];
                      const day = days[new Date(bookingData.date).getDay()];
                      if (bookingData.service === 'minipaket') return ` — Varannan ${day}`;
                      if (bookingData.service === 'package') return ` — Varje ${day}`;
                      if (bookingData.subscription === 'weekly') return ` — Varje ${day}`;
                      if (bookingData.subscription === 'biweekly') return ` — Varannan ${day}`;
                      if (bookingData.subscription === 'triweekly') return ` — Var tredje ${day}`;
                      return '';
                    })()}
                  </span></p>
<p><span style={{ color: '#888' }}>Adress:</span> <span className="font-semibold">{bookingData.address}, {bookingData.city}</span></p>
<p className="text-xl font-bold pt-2" style={{ color: '#C9A84C' }}>
Du betalar: {Math.round(bookingData.finalPrice).toLocaleString()} kr {hasRut(bookingData.service) ? '(efter 50% RUT)' : ''}{bookingData.service === 'package' ? '/mån' : ''}
</p>
</div>
</div>
{hasRut(bookingData.service) && (
<div className="p-4 rounded-xl mb-4" style={{ backgroundColor: '#fef9ec', border: '1px solid #fde68a' }}>
<p className="text-sm font-semibold" style={{ color: '#92400e' }}>⚠️ Kolla din email — vi har skickat ett viktigt mail om RUT-avdraget!</p>
</div>
)}
<div className="p-4 rounded-xl mb-6" style={{ backgroundColor: '#dcfce7', border: '1px solid #86efac' }}>
<p className="text-sm" style={{ color: '#16a34a' }}><strong>✅ Bekräftelse skickas till {bookingData.email}</strong></p>
</div>
<div className="flex flex-col gap-3">
<button onClick={resetBooking} className="w-full px-8 py-4 rounded-full font-bold" style={{ backgroundColor: '#1a1a2e', color: '#C9A84C', border: '2px solid #C9A84C' }}>
Boka Ny Städning
</button>
{!currentUser && onNavigate && (
<>
<div className="flex items-center gap-3">
<div className="flex-1 h-px" style={{ backgroundColor: '#e8e4d9' }}></div>
<span className="text-xs" style={{ color: '#aaa' }}>eller</span>
<div className="flex-1 h-px" style={{ backgroundColor: '#e8e4d9' }}></div>
</div>
<button onClick={() => onNavigate('login')} className="w-full px-8 py-4 rounded-full font-bold" style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>
⭐ Skapa konto — spara dina uppgifter
</button>
</>
)}
</div>
</div>
)}
</div>

{step !== 1 && step !== 99 && (
  <div className="lg:hidden mt-6 p-5 rounded-2xl" style={{ backgroundColor: '#fff', border: '1px solid #e8e4d9' }}>
    <label className="block text-sm font-bold mb-2">Har du en rabattkod?</label>
    <div className="flex gap-2">
      <input type="text" value={discountCode}
        onChange={e => { setDiscountCode(e.target.value); setDiscountError(''); }}
        onKeyDown={e => { if (e.key === 'Enter') handleApplyDiscount(); }}
        placeholder="Ange kod..." autoComplete="off"
        style={{ flex: 1, padding: '10px 12px', borderRadius: '10px', border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4', color: '#1a1a2e', outline: 'none', fontSize: '16px', minWidth: 0 }} />
      <button type="button" onClick={handleApplyDiscount}
        style={{ padding: '10px 16px', borderRadius: '10px', backgroundColor: '#1a1a2e', color: '#C9A84C', fontWeight: 'bold', border: 'none' }}>OK</button>
    </div>
    {discountError && <p className="text-xs mt-2" style={{ color: '#dc2626' }}>{discountError}</p>}
    {appliedDiscount && <p className="text-xs mt-2" style={{ color: '#16a34a' }}>✅ {appliedDiscount.code} — {appliedDiscount.discount_percent}% rabatt!</p>}
  </div>
)}


{/* Summary sidebar */}
{step !== 1 && step !== 99 && (
<div>
<BookingSummary
bookingData={bookingData}
serviceNames={serviceNames}
discountCode={discountCode}
discountError={discountError}
appliedDiscount={appliedDiscount}
discountAmount={discountAmount}
finalAfterCode={finalAfterCode}
/>
{/* Discount input rendered HERE in parent — never loses focus */}
<div className="hidden lg:block" style={{ width: '300px' }}>
<div style={{ marginTop: '-16px', backgroundColor: '#fff', border: '1px solid #e8e4d9', borderBottomLeftRadius: '20px', borderBottomRightRadius: '20px', padding: '0 24px 24px' }}>
<div style={{ borderTop: '1px solid #e8e4d9', paddingTop: '16px' }}>
<label className="block text-xs font-bold mb-1" style={{ color: '#888' }}>Rabattkod</label>
<div className="flex gap-2">
<input
type="text"
value={discountCode}
onChange={e => { setDiscountCode(e.target.value); setDiscountError(''); }}
onKeyDown={e => { if (e.key === 'Enter') handleApplyDiscount(); }}
placeholder="Ange kod..."
autoComplete="off"
style={{ flex: 1, padding: '8px 12px', borderRadius: '10px', border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4', color: '#1a1a2e', outline: 'none', fontSize: '14px', minWidth: 0 }}
/>
<button type="button" onClick={handleApplyDiscount}
style={{ padding: '8px 14px', borderRadius: '10px', backgroundColor: '#1a1a2e', color: '#C9A84C', fontWeight: 'bold', fontSize: '14px', flexShrink: 0, border: 'none', cursor: 'pointer' }}>
OK
</button>
</div>
{discountError && <p className="text-xs mt-1" style={{ color: '#dc2626' }}>{discountError}</p>}
{appliedDiscount && <p className="text-xs mt-1" style={{ color: '#16a34a' }}>✅ {appliedDiscount.code} — {appliedDiscount.discount_percent}% rabatt!</p>}
</div>

</div>
</div>
{/* Mobile discount */}
<div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 px-4 pb-20"
style={{ pointerEvents: 'none' }}>
</div>
</div>
)}
</div>
</div>
);
};

export default BookingForm;